import { env } from "@/config/env";

import { withBasePath } from "@/lib/portal/base-path";

/** In-app HashRouter path for Glofox docs home. */
export const GLOFOX_DOCS_HOME_ROUTE = "/glofox";

/** In-app HashRouter path for Glofox API Reference. */
export const GLOFOX_API_REFERENCE_ROUTE = "/glofox/api-reference";

/** Normalize docs site base (always trailing slash, except empty → `/api-portal-docs/`). */
export function normalizeDocsBaseUrl(value: string | undefined): string {
  const raw = (value ?? "/api-portal-docs/").trim() || "/api-portal-docs/";
  if (raw === "/") return "/";
  return raw.endsWith("/") ? raw : `${raw}/`;
}

export function getDocsBaseUrl(): string {
  return normalizeDocsBaseUrl(env.docsBaseUrl);
}

/** Join docs base with a path under the Markdoc site (no leading slash required). */
export function withDocsBaseUrl(path: string): string {
  const base = getDocsBaseUrl().replace(/\/$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  if (!base) return suffix;
  if (suffix === base || suffix.startsWith(`${base}/`)) return suffix;
  return `${base}${suffix}`;
}

/**
 * Public portal hash URL for an in-app route (e.g. `/glofox` → `/api-portal/#/glofox`).
 * Use for plain `<a href>` that must leave/enter the SPA with HashRouter.
 */
export function withPortalHashPath(routePath: string): string {
  const route = routePath.startsWith("/") ? routePath : `/${routePath}`;
  const base = withBasePath("/").replace(/\/$/, "");
  return base ? `${base}/#${route}` : `/#${route}`;
}

/** Public URL for Glofox docs home (`/api-portal/#/glofox`). */
export function getGlofoxDocsHomeHref(): string {
  return withPortalHashPath(GLOFOX_DOCS_HOME_ROUTE);
}

/** Public URL for Glofox API Reference (`/api-portal/#/glofox/api-reference`). */
export function getGlofoxApiReferenceHref(): string {
  return withPortalHashPath(GLOFOX_API_REFERENCE_ROUTE);
}

/**
 * Map SPA docs routes to Markdoc asset URLs (iframe `src`).
 * Returns null when the path is not a known docs route (app pages).
 */
export function resolveDocsFrameSrc(pathname: string): string | null {
  const path = pathname.replace(/\/$/, "") || "/";

  const exact: Record<string, string> = {
    "/glofox": withDocsBaseUrl("/glofox/docs/"),
    "/glofox/get-started": withDocsBaseUrl("/glofox/docs/"),
    "/glofox/authentication": withDocsBaseUrl(
      "/glofox/docs/authentication.html",
    ),
    "/glofox/quick-reference": withDocsBaseUrl(
      "/glofox/docs/quick-reference.html",
    ),
    "/glofox/common-concepts": withDocsBaseUrl(
      "/glofox/docs/common-concepts.html",
    ),
    /** Legacy route; error guide moved into API Reference. */
    "/glofox/error-handling": `${withDocsBaseUrl("/glofox/api-reference.html")}#error-handling`,
    "/glofox/webhooks": withDocsBaseUrl("/glofox/docs/cdc-webhooks.html"),
    "/glofox/request-access": withDocsBaseUrl(
      "/glofox/docs/request-access.html",
    ),
    "/glofox/faq": withDocsBaseUrl("/glofox/docs/faq.html"),
    "/glofox/api-reference": withDocsBaseUrl("/glofox/api-reference.html"),
    "/glofox/api/openapi-reference.html": withDocsBaseUrl(
      "/glofox/api-reference.html",
    ),
  };

  if (exact[path]) return exact[path];

  const flowMatch = path.match(/^\/glofox\/flows\/([^/]+)$/);
  if (flowMatch) {
    return withDocsBaseUrl(`/glofox/docs/flows/${flowMatch[1]}.html`);
  }

  return null;
}

/**
 * @deprecated Prefer `resolveDocsFrameSrc` — same Markdoc asset mapping.
 */
export function resolveLegacyDocsRedirect(pathname: string): string | null {
  return resolveDocsFrameSrc(pathname);
}

/**
 * Convert a Markdoc asset path (or full docs URL) back to an in-app SPA route.
 * Used when `from=` still carries legacy `/api-portal-docs/…` bookmarks.
 */
export function markdocPathToSpaRoute(path: string): string | null {
  const docsBase = getDocsBaseUrl().replace(/\/$/, "");
  let normalized = path.trim();
  if (!normalized.startsWith("/")) return null;

  if (docsBase && normalized.startsWith(docsBase)) {
    normalized = normalized.slice(docsBase.length) || "/";
  } else if (normalized.startsWith("/api-portal-docs")) {
    normalized = normalized.replace(/^\/api-portal-docs/, "") || "/";
  }

  normalized = normalized.replace(/\/$/, "") || "/";

  if (normalized === "/glofox/docs" || normalized === "/glofox/docs/index.html") {
    return GLOFOX_DOCS_HOME_ROUTE;
  }
  if (normalized === "/glofox/api-reference.html") {
    return GLOFOX_API_REFERENCE_ROUTE;
  }

  const auth = normalized.match(/^\/glofox\/docs\/([^/]+)\.html$/);
  if (auth) {
    const slug = auth[1];
    const guideMap: Record<string, string> = {
      authentication: "/glofox/authentication",
      "quick-reference": "/glofox/quick-reference",
      "common-concepts": "/glofox/common-concepts",
      "api-errors": GLOFOX_API_REFERENCE_ROUTE,
      "cdc-webhooks": "/glofox/webhooks",
      "request-access": "/glofox/request-access",
      faq: "/glofox/faq",
    };
    return guideMap[slug] ?? null;
  }

  const flow = normalized.match(/^\/glofox\/docs\/flows\/([^/]+)\.html$/);
  if (flow) {
    return `/glofox/flows/${flow[1]}`;
  }

  return null;
}
